import mgr from '@core/services/SecurityService';
import {sanitizeInternalRedirect} from '@core/utils/sanitizeInternalRedirect';
import {useUserStore} from '@core/store/UserStore';
import {RouteLocationNormalized} from 'vue-router';
import snackbarService from '@core/services/SnackbarService';
import i18n from '@core/plugins/i18n';
import httpService from '@core/services/AxiosApiService';
import type {User} from 'oidc-client-ts';

/** Facade around OIDC UserManager; Pinia must be active before use (normal app bootstrap). */
class AuthService {
  private get userStore() {
    return useUserStore();
  }

  async getUser() {
    return await mgr.getUser();
  }

  /** Returns a non-expired OIDC user, silently renewing the token when needed. */
  async ensureValidUser(): Promise<User | null> {
    let user = await mgr.getUser();
    if (!user) {
      return null;
    }
    if (user.expired) {
      try {
        user = await mgr.signinSilent();
      } catch (error) {
        console.error('Silent token renewal failed', error);
        return null;
      }
    }
    if (user?.access_token) {
      httpService.setAccessToken(user.access_token);
    }
    return user;
  }

  private async signinRedirect(
    to: string | undefined = undefined,
  ): Promise<void> {
    const safe = to == null ? null : sanitizeInternalRedirect(to);
    const callbackPath =
      safe == null
        ? '/callback'
        : `/callback?redirect=${encodeURIComponent(safe)}`;
    return await mgr.signinRedirect({
      redirect_uri: globalThis.location.origin + callbackPath,
    });
  }

  isAuthenticated(): boolean {
    const store = this.userStore;
    return store.loggedIn;
  }

  async login(redirectPath?: string): Promise<void> {
    try {
      const user = await this.getUser();
      await (user
        ? this.userStore.login(user)
        : this.signinRedirect(redirectPath));
    } catch (error) {
      console.error('AuthService.login failed:', error);
      throw error;
    }
  }

  async logout(): Promise<void> {
    const user = await mgr.getUser();
    if (!user || !this.isAuthenticated()) {
      return;
    }

    // Sign out (user exists)
    await mgr.signoutRedirect({
      id_token_hint: user.id_token,
    });
  }

  async redirectIfUnauthenticated(
    to: RouteLocationNormalized,
  ): Promise<false | undefined> {
    const user = await this.getUser();
    if (user) {
      return undefined;
    }
    if (to.path.includes('/callback')) {
      return undefined;
    }
    await this.signinRedirect(to.fullPath);
    return false;
  }

  /** @returns whether OIDC completed and the user session was stored */
  async handleCallback(): Promise<boolean> {
    try {
      const user = await mgr.signinRedirectCallback();
      if (!user) {
        return false;
      }
      this.userStore.setSession(user);
      return true;
    } catch (error) {
      console.error('OIDC signinRedirectCallback failed:', error);
      snackbarService.showSadSnackbar(i18n.global.t('core.errorHappened'));
      return false;
    }
  }
}

export default new AuthService();
